import { useEffect, useState } from "react";
import { useOasisBackend } from "../../hooks/useOasisBackend";
import { Entity, EntityCollection, OasisSearchResult, Query, QueryMatch, MatchTypeEnum } from "../../api/OasisBackendApi";
import { toast, ToastContainer } from "react-toastify";
import TesseractLoader from "../ui/tesseract-loader";
import { useParams, useSearchParams } from "react-router-dom";

// Match type options with colors
const MATCH_TYPE_OPTIONS = [
    { value: MatchTypeEnum.PerfectMatch, label: "Perfect Match", color: "green" },
    { value: MatchTypeEnum.GoodMatch, label: "Good Match", color: "cyan" },
    { value: MatchTypeEnum.Match, label: "Match", color: "blue" },
    { value: MatchTypeEnum.PartialMatch, label: "Partial Match", color: "yellow" },
    { value: MatchTypeEnum.Irrelevant, label: "Irrelevant", color: "gray" },
    { value: MatchTypeEnum.Contradiction, label: "Contradiction", color: "red" }
];

const getMatchTypeColor = (matchType: string) => {
    const option = MATCH_TYPE_OPTIONS.find(opt => opt.value === matchType);
    return option?.color || "gray";
};

const getMatchTypeLabel = (matchType: string) => {
    const option = MATCH_TYPE_OPTIONS.find(opt => opt.value === matchType);
    return option?.label || "Not Set";
};

// Reusable component for rendering search results
const SearchResult = ({ 
    isOwner,
    result, 
    embeddingType,
    uiMapping,
    onUpdateMatchType,
    currentQuery,
    existingQuery
}: { 
    isOwner?: boolean,
    result: Entity, 
    embeddingType: string,
    uiMapping: {
        [key: string]: string
    },
    onUpdateMatchType: (entityId: string, matchType: string) => void,
    currentQuery: string,
    existingQuery: Query | null
}) => {
    const [isContentExpanded, setIsContentExpanded] = useState(false);
    
    // Get current match type for this entity
    const currentMatch = existingQuery?.query_matches?.find(match => match.entity_id === result.id);
    const currentMatchType = currentMatch?.match_type;
    
    let imageUrls: string[] = [];
    try {
        if (uiMapping?.image_urls) {
            const imageData = result.content[uiMapping.image_urls];
            if (typeof imageData === 'string') {
                // Try to parse as JSON first
                try {
                    const parsed = JSON.parse(imageData.replace(/'/g, '"'));
                    if (typeof parsed === 'string') {
                        // Parsed result is a single URL string
                        imageUrls = [parsed];
                    } else if (Array.isArray(parsed)) {
                        // Parsed result is an array
                        imageUrls = parsed;
                    } else {
                        imageUrls = [];
                    }
                } catch {
                    // If JSON parsing fails, treat as single URL and convert to array
                    imageUrls = [imageData];
                }
            } else if (Array.isArray(imageData)) {
                imageUrls = imageData;
            } else {
                imageUrls = [];
            }
        }
    } catch (error) {
        console.error("Error parsing image urls", error);
        imageUrls = [];
    }
    return (
        <div className="bg-gray-800 p-4 rounded-md mb-2 flex flex-col md:flex-row gap-4">
            <div className="flex flex-col w-full md:w-1/2">
                <div className="mb-2">
                    {uiMapping?.title && <h3 className="text-lg mb-2">{result.content[uiMapping.title]}</h3>}
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            {currentMatchType && (
                                <span className={`inline-flex items-center px-2 py-1 text-xs font-medium border rounded-full ${
                                    getMatchTypeColor(currentMatchType) === 'green' ? 'bg-green-900/30 text-green-300 border-green-700/50' :
                                    getMatchTypeColor(currentMatchType) === 'cyan' ? 'bg-cyan-900/30 text-cyan-300 border-cyan-700/50' :
                                    getMatchTypeColor(currentMatchType) === 'blue' ? 'bg-blue-900/30 text-blue-300 border-blue-700/50' :
                                    getMatchTypeColor(currentMatchType) === 'yellow' ? 'bg-yellow-900/30 text-yellow-300 border-yellow-700/50' :
                                    getMatchTypeColor(currentMatchType) === 'red' ? 'bg-red-900/30 text-red-300 border-red-700/50' :
                                    'bg-gray-900/30 text-gray-300 border-gray-700/50'
                                }`}>
                                    {getMatchTypeLabel(currentMatchType)}
                                </span>
                            )}
                        </div>
                        {isOwner && (
                            <div className="flex items-center gap-2">
                                <select
                                    value={currentMatchType || ''}
                                    onChange={(e) => onUpdateMatchType(result.id, e.target.value)}
                                    className="px-3 py-1.5 rounded-md text-sm font-medium bg-gray-700 text-gray-200 border border-gray-600 hover:border-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">Not Set</option>
                                    {MATCH_TYPE_OPTIONS.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>
                </div>
                {result.distance && <p className="text-sm text-gray-400">Distance: {parseFloat(result.distance).toFixed(4)}</p>}
                {imageUrls && imageUrls.length > 0 && (
                    <img 
                        src={imageUrls[0]} 
                        alt={uiMapping?.title ? result.content[uiMapping.title] : 'Product image'} 
                        className="max-w-full sm:max-w-[300px] h-auto object-contain" 
                    />
                )}
                {uiMapping?.description && <p className="my-4 text-sm h-40 overflow-y-auto">{result.content[uiMapping.description]}</p>}
                
                {/* Accordion for JSON content */}
                <div className="border border-gray-600 rounded-md">
                    <button
                        onClick={() => setIsContentExpanded(!isContentExpanded)}
                        className="w-full px-3 py-2 text-left text-sm font-medium text-gray-300 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-t-md"
                    >
                        <div className="flex items-center justify-between">
                            <span>Raw Content Data</span>
                            <svg
                                className={`w-4 h-4 transform transition-transform ${isContentExpanded ? 'rotate-180' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </div>
                    </button>
                    {isContentExpanded && (
                        <div className="px-3 py-2 border-t border-gray-600">
                            <pre className="whitespace-pre-wrap text-xs sm:text-sm overflow-auto max-h-60">
                                {JSON.stringify(result.content, null, 2)}
                            </pre>
                        </div>
                    )}
                </div>
            </div>
            <div className="flex flex-col w-full md:w-1/2">
                <div className="border border-gray-600 rounded-md">
                    <div className="px-3 py-2 bg-gray-700 border-b border-gray-600 rounded-t-md">
                        <h4 className="text-sm font-medium text-gray-200">Oasis Embedding</h4>
                    </div>
                    <div className="p-2 sm:p-3 max-h-96 overflow-y-auto">
                        <div className="flex flex-wrap gap-1 sm:gap-2">
                            {Object.entries(result.embedding?.metadata?.entity_dimension_index || {}).map(([key, value]) => (
                                <div key={key} className="bg-gray-700 p-1.5 sm:p-2 rounded border border-gray-600 flex-shrink-0" style={{ width: '140px' }}>
                                    <div className="text-xs text-gray-400 font-medium mb-0.5 sm:mb-1 break-words">{key}</div>
                                    <div className="text-xs font-mono text-gray-200 break-all">
                                        {typeof value === 'number' ? value.toFixed(4) : String(value)}
                                    </div>
                                </div>
                            ))}
                        </div>
                        {Object.keys(result.embedding?.metadata?.entity_dimension_index || {}).length === 0 && (
                            <div className="text-sm text-gray-500 italic">No embedding data available</div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// Helper function to calculate match type statistics
const calculateMatchStats = (existingQuery: Query | null, results: any[]) => {
    if (!existingQuery?.query_matches || existingQuery.query_matches.length === 0 || results.length === 0) {
        return null;
    }
    
    // Match type weights from incremental.py
    const matchMultipliers: { [key: string]: number } = {
        'perfect_match': 5.0,
        'good_match': 3.0,
        'match': 2.0,
        'partial_match': 1.0,
        'irrelevant': -2.0,
        'contradiction': -5.0
    };
    
    // Order from perfect match to contradiction
    const matchTypeOrder = [
        'perfect_match',
        'good_match', 
        'match',
        'partial_match',
        'irrelevant',
        'contradiction'
    ];
    
    const matchCounts: { [key: string]: number } = {};
    const matchRankings: { [key: string]: number[] } = {};
    const matchScores: { [key: string]: number[] } = {};
    
    // Create a map of entity_id to rank in results
    const entityRankMap: { [key: string]: number } = {};
    results.forEach((result, index) => {
        entityRankMap[result.id] = index + 1; // rank starts from 1
    });
    
    // Only process matches that are actually in the results
    existingQuery.query_matches.forEach((match) => {
        const entityId = match.entity_id;
        const matchType = match.match_type;
        
        // Only include if this entity is in the results
        if (entityRankMap[entityId]) {
            const rank = entityRankMap[entityId];
            const multiplier = matchMultipliers[matchType] || 0;
            const weightedScore = rank * multiplier;
            
            matchCounts[matchType] = (matchCounts[matchType] || 0) + 1;
            if (!matchRankings[matchType]) {
                matchRankings[matchType] = [];
            }
            if (!matchScores[matchType]) {
                matchScores[matchType] = [];
            }
            matchRankings[matchType].push(rank);
            matchScores[matchType].push(weightedScore);
        }
    });
    
    // Create stats in the specified order
    const stats = matchTypeOrder
        .filter(matchType => matchCounts[matchType] > 0)
        .map(matchType => {
            const rankings = matchRankings[matchType];
            const scores = matchScores[matchType];
            const avgRanking = rankings.reduce((sum, rank) => sum + rank, 0) / rankings.length;
            const avgScore = scores.reduce((sum, score) => sum + score, 0) / scores.length;
            
            return {
                matchType,
                count: matchCounts[matchType],
                avgRanking: avgRanking.toFixed(1),
                avgScore: avgScore.toFixed(2)
            };
        });
    
    // Calculate average score across all match types
    const allScores = Object.values(matchScores).flat();
    const averageScore = allScores.length > 0 ? allScores.reduce((sum, score) => sum + score, 0) / allScores.length : 0;
    
    return {
        stats,
        totalScore: averageScore.toFixed(2)
    };
};

// Reusable component for search type section
const SearchTypeSection = ({ 
    isOwner,
    title, 
    loading, 
    results, 
    rerank, 
    setRerank, 
    embeddingType,
    uiMapping,
    onUpdateMatchType,
    currentQuery,
    existingQuery
}: { 
    isOwner?: boolean,
    title: string, 
    loading: boolean, 
    results: any[], 
    rerank: boolean, 
    setRerank: (value: boolean) => void, 
    embeddingType: string,
    uiMapping: {
        [key: string]: string
    },
    onUpdateMatchType: (entityId: string, matchType: string) => void,
    currentQuery: string,
    existingQuery: Query | null
}) => {
    const matchStats = calculateMatchStats(existingQuery, results);
    
    return (
        <div className="flex-1 w-full lg:w-1/2">
            <div className="flex items-center gap-2 mb-2">
                <h2 className="font-bold">{title}</h2>
                {loading && <TesseractLoader className="w-4 h-4" />}
                <label className="flex items-center gap-1">
                    <input
                        type="checkbox"
                        checked={rerank}
                        onChange={(e) => setRerank(e.target.checked)}
                    />
                    Rerank
                </label>
            </div>
            {matchStats && (
                <div className="mb-3">
                    <div className="bg-gray-700 p-2 rounded text-xs">
                        <div className="flex items-center justify-between mb-1">
                            <div className="font-medium text-gray-300">Match Type Statistics</div>
                            <div className="text-sm font-bold text-gray-200">
                                Avg Score: <span className={parseFloat(matchStats.totalScore) >= 0 ? 'text-green-400' : 'text-red-400'}>{matchStats.totalScore}</span>
                            </div>
                        </div>
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="text-gray-400">
                                    <th className="text-left">Type</th>
                                    <th className="text-right">Count</th>
                                    <th className="text-right">Avg Rank</th>
                                    <th className="text-right">Score</th>
                                </tr>
                            </thead>
                            <tbody>
                                {matchStats.stats.map(({ matchType, count, avgRanking, avgScore }) => (
                                    <tr key={matchType} className="text-gray-200">
                                        <td className="text-left">{getMatchTypeLabel(matchType)}</td>
                                        <td className="text-right">{count}</td>
                                        <td className="text-right">{avgRanking}</td>
                                        <td className="text-right">{avgScore}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
            {results.map((result) => (
                <SearchResult 
                    isOwner={isOwner}
                    key={result.id || result.content} 
                    result={result} 
                    embeddingType={embeddingType}
                    uiMapping={uiMapping}
                    onUpdateMatchType={onUpdateMatchType}
                    currentQuery={currentQuery}
                    existingQuery={existingQuery}
                />
            ))}
        </div>
    );
};

// Inner SearchPlayground component that receives entityCollection as a prop
const SearchPlaygroundInner = ({ 
    isOwner, 
    Database_id, 
    oasis_enabled, 
    entityCollection 
}: { 
    isOwner?: boolean, 
    Database_id: string, 
    oasis_enabled: boolean,
    entityCollection: EntityCollection
}) => {
    const [query, setQuery] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const db = useOasisBackend();
    const [semanticResults, setSemanticResults] = useState<Entity[]>([]);
    const [oasisResults, setOasisResults] = useState<OasisSearchResult>();
    const [selectedSearchTypes, setSelectedSearchTypes] = useState<string[]>(oasis_enabled ? ["semantic", "oasis"] : ["semantic"]);
    const [semanticRerank, setSemanticRerank] = useState(false);
    const [oasisRerank, setOasisRerank] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const [existingQuery, setExistingQuery] = useState<Query | null>(null);

    useEffect(() => {
        const queryFromUrl = searchParams.get('q');
        console.log("queryFromUrl", queryFromUrl);
        if (queryFromUrl) {
            setQuery(queryFromUrl);
            handleSearch(queryFromUrl);
        }
    }, []); 

    // Clear existing query when query text changes
    useEffect(() => {
        setExistingQuery(null);
    }, [query]);

    const handleUpdateMatchType = async (entityId: string, matchType: string) => {
        if (!query.trim()) {
            toast.error("Please enter a query first");
            return;
        }

        if (!existingQuery) {
            toast.error("Please search for a query first to create it, then you can set match types");
            return;
        }

        try {
            // Get current query matches
            const currentMatches = existingQuery?.query_matches || [];
            
            // Update or add the match for this entity
            let updatedMatches: QueryMatch[];
            const existingMatchIndex = currentMatches.findIndex(match => match.entity_id === entityId);
            
            if (matchType === '') {
                // Remove the match if "Not Set" is selected
                updatedMatches = currentMatches.filter(match => match.entity_id !== entityId);
            } else {
                // Update or add the match
                const newMatch: QueryMatch = {
                    entity_id: entityId,
                    match_type: matchType as MatchTypeEnum
                };
                
                if (existingMatchIndex >= 0) {
                    // Update existing match
                    updatedMatches = [...currentMatches];
                    updatedMatches[existingMatchIndex] = newMatch;
                } else {
                    // Add new match
                    updatedMatches = [...currentMatches, newMatch];
                }
            }

            // Update existing query
            const updatedQuery = await db.endpoints.api.apiQueriesUpdate(existingQuery.id, {
                query_matches: updatedMatches
            });
            setExistingQuery(updatedQuery.data);
            
            const action = matchType === '' ? "removed" : matchType === existingQuery?.query_matches?.find(m => m.entity_id === entityId)?.match_type ? "updated" : "set";
            toast.success(`Entity match type ${action} successfully!`);
        } catch (error) {
            console.error("Error updating entity match type:", error);
            toast.error("Failed to update entity match type");
        }
    };

    let availableSearchTypes: { label: string, value: string }[] = [];

    if (oasis_enabled) {
        availableSearchTypes = [
            {
                label: "Semantic",
                value: "semantic"
            },
            {
                label: "Oasis",
                value: "oasis"
            }
        ]
    }else{
        availableSearchTypes = [{
            label: "Semantic",
            value: "semantic"
        }]
    }

    const handleSearchTypeChange = (searchType: string) => {
        setSelectedSearchTypes(prev => {
            if (prev.includes(searchType)) {
                return prev.filter(type => type !== searchType);
            }
            return [...prev, searchType];
        });
    };

    const handleSearch = async (argQuery: string = "") => {
        const queryToUse = argQuery.length > 0 ? argQuery : query;
        if (queryToUse.length === 0) {
            toast.error("Please enter a query");
            return;
        }
        setLoading(true);
        setError(null);
        // Create an array of promises for all selected search types
        const searchPromises = [];

        if (selectedSearchTypes.includes('oasis')) {
            setOasisResults(undefined);
            // Assuming there's an Oasis search endpoint
            const oasisPromise = db.endpoints.api.apiDatabaseSearchCreate(Database_id, { 
                query: queryToUse, 
                type: "oasis",
                rerank: oasisRerank 
            })
                .then(response => setOasisResults(response.data.oasis));
            searchPromises.push(oasisPromise);
        }
        
        if (selectedSearchTypes.includes('semantic')) {
            console.log("Semantic Search", queryToUse, semanticRerank);
            setSemanticResults([]);
            const semanticPromise = db.endpoints.api.apiDatabaseSearchCreate(Database_id, { 
                query: queryToUse, 
                type: "semantic",
                rerank: semanticRerank 
            })
                .then(response => setSemanticResults(response.data.semantic));
            searchPromises.push(semanticPromise);
        }

        // Check if a query with this text already exists
        console.log("Checking if query exists", queryToUse.trim(), entityCollection?.id);
        if (queryToUse.trim() && entityCollection?.id) {
            const queryCheckPromise = db.endpoints.api.apiEntityCollectionQueryByTextRetrieve(
                entityCollection.id,
                { text: queryToUse.trim() }
            )
                .then(queryResponse => {
                    console.log("Existing query found:", queryResponse.data);
                    setExistingQuery(queryResponse.data);
                })
                .catch(error => {
                    // Query doesn't exist, which is fine
                    console.log("No existing query found for:", queryToUse.trim());
                    setExistingQuery(null);
                });
            searchPromises.push(queryCheckPromise);
        }

        
        // Wait for all search requests to complete
        await Promise.all(searchPromises);
        setSearchParams(params => ({...Object.fromEntries(params.entries()), q: queryToUse}));

        setLoading(false);
    };

    console.log(oasisResults);

    const ui_mapping = entityCollection.ui_mapping;
    
    return (
        <div className="flex flex-col gap-4 p-4 sm:p-8">
            <input 
                className="p-2 rounded-md bg-gray-800 text-white outline-none" 
                type="text" 
                value={query} 
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        handleSearch();
                    }
                }}
            />   
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 items-start sm:items-center">
                {availableSearchTypes.map((type) => (
                    <label key={type.value} className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            checked={selectedSearchTypes.includes(type.value)}
                            onChange={() => handleSearchTypeChange(type.value)}
                        />
                        {type.label}
                    </label>
                ))}
            </div>
            <div className="flex flex-row gap-2">
                    <button 
                        className={`p-2 rounded-md ${selectedSearchTypes.length === 0 ? 'bg-gray-300 cursor-not-allowed' : 'bg-gray-400 hover:bg-gray-500'}`}
                        onClick={() => handleSearch()}
                        disabled={selectedSearchTypes.length === 0}
                    >
                        Search
                    </button>
            </div>

            {oasisResults && (
                <div className="bg-gray-800 p-4 rounded-md mb-4">
                    <h3 className="font-bold mb-2">Query Embedding</h3>
                    <div className="max-h-80 overflow-auto">
                        {oasisResults.oasis_query_embedding ? (
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                                {Object.entries(oasisResults.oasis_query_embedding).map(([key, value]) => {
                                    const ftv = oasisResults.oasis_ftv_embedding?.[key];
                                    const ftvValue = typeof ftv === 'number' ? ftv : 1.0;
                                    const isExcluded = ftvValue === 0;
                                    return (
                                        <div key={key} className={`flex flex-col bg-gray-700 p-2 rounded border ${isExcluded ? 'border-gray-600 opacity-50' : 'border-gray-600'}`}>
                                            <span className="text-xs text-gray-400 truncate" title={key}>{key}</span>
                                            <span className={`font-mono text-xs sm:text-sm ${isExcluded ? 'text-gray-500' : ''}`}>
                                                {typeof value === 'number' ? value.toFixed(4) : String(value)}
                                            </span>
                                            {oasisResults.oasis_ftv_embedding && (
                                                <div className="mt-1 flex items-center gap-1">
                                                    <div className="flex-1 h-1.5 bg-gray-600 rounded-full overflow-hidden">
                                                        <div 
                                                            className={`h-full rounded-full ${
                                                                ftvValue >= 0.8 ? 'bg-blue-400' :
                                                                ftvValue >= 0.5 ? 'bg-yellow-400' :
                                                                ftvValue > 0 ? 'bg-orange-400' :
                                                                'bg-gray-500'
                                                            }`}
                                                            style={{ width: `${ftvValue * 100}%` }}
                                                        />
                                                    </div>
                                                    <span className={`text-[10px] font-mono w-8 text-right ${
                                                        isExcluded ? 'text-gray-500' : 'text-gray-400'
                                                    }`}>
                                                        {ftvValue.toFixed(1)}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <p>No query embedding</p>
                        )}
                    </div>
                </div>
            )}
            
            <div className="flex flex-col lg:flex-row gap-4">
                {selectedSearchTypes.includes('semantic') && (
                    <SearchTypeSection
                        isOwner={isOwner}
                        title="Semantic Results"
                        loading={loading}
                        results={semanticResults}
                        rerank={semanticRerank}
                        setRerank={setSemanticRerank}
                        embeddingType="Semantic"
                        uiMapping={ui_mapping}
                        onUpdateMatchType={handleUpdateMatchType}
                        currentQuery={query}
                        existingQuery={existingQuery}
                    />
                )}
                
                {selectedSearchTypes.includes('oasis') && (
                    <SearchTypeSection
                        isOwner={isOwner}
                        title="Oasis Results"
                        loading={loading}
                        results={oasisResults?.results || []}
                        rerank={oasisRerank}
                        setRerank={setOasisRerank}
                        embeddingType="Oasis"
                        uiMapping={ui_mapping}
                        onUpdateMatchType={handleUpdateMatchType}
                        currentQuery={query}
                        existingQuery={existingQuery}
                    />
                )}
            </div>
        </div>
    )
}

// Parent wrapper component that handles entityCollection retrieval
const SearchPlayground = ({ isOwner, Database_id, oasis_enabled }: { isOwner?: boolean, Database_id: string, oasis_enabled: boolean }) => {
    const [entityCollection, setEntityCollection] = useState<EntityCollection | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const db = useOasisBackend();

    const fetchEntityCollection = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await db.endpoints.api.apiDatabaseEntityCollectionRetrieve(Database_id);
            setEntityCollection(response.data);
        } catch (err) {
            console.error("Error fetching entity collection:", err);
            setError("Failed to load entity collection");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEntityCollection();
    }, [Database_id]);

    if (loading) {
        return <div className="flex items-center justify-center p-8">Loading entity collection...</div>;
    }

    if (error) {
        return <div className="flex items-center justify-center p-8 text-red-500">Error: {error}</div>;
    }

    if (!entityCollection) {
        return <div className="flex items-center justify-center p-8">No entity collection found</div>;
    }

    return (
        <SearchPlaygroundInner
            isOwner={isOwner}
            Database_id={Database_id}
            oasis_enabled={oasis_enabled}
            entityCollection={entityCollection}
        />
    );
};

export default SearchPlayground;